/**
 * The one definition of what makes an acceptable password.
 *
 * Deliberately free of "use client" and of any server-only import, so the
 * signup form, the reset form and the server actions behind them can all share
 * it. It previously existed twice — as firstPolicyError() inside the client
 * component that renders the checklist, and again as passwordPolicyError()
 * inside the reset action — which meant the rules a user was shown and the
 * rules actually enforced were free to drift apart.
 */

export type PasswordChecks = {
  length: boolean;
  upper: boolean;
  number: boolean;
  special: boolean;
};

export function passwordChecks(pw: string): PasswordChecks {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    // "special" is anything that is not a letter, digit, underscore or space.
    special: /[^\w\s]/.test(pw),
  };
}

/** The first unmet requirement, or null when the password is acceptable. */
export function firstPolicyError(pw: string): string | null {
  const c = passwordChecks(pw);
  if (!c.length) return "Password must be at least 8 characters.";
  if (!c.upper) return "Password must include at least 1 uppercase letter.";
  if (!c.number) return "Password must include at least 1 number.";
  if (!c.special) return "Password must include at least 1 special character.";
  return null;
}
